/**
 * BpmChangeDialog — asks how a BPM change should treat the objects already in the map. Shown only when the
 * map holds objects (in any difficulty) and the mapper asked for a new BPM; nothing changes until a choice
 * is made. The default is to keep every object on its beat, so it moves with the tempo.
 */

import React, { useEffect, useRef } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { color, font, ui, z } from '../ui/theme';

export default function BpmChangeDialog() {
  const pending = useEditorStore(s => s.pendingBpmChange);
  const current = useEditorStore(s => s.beatmap?.bpm);
  const apply = useEditorStore(s => s.applyBpmChange);
  const cancel = useEditorStore(s => s.cancelBpmChange);

  const cardRef = useRef<HTMLDivElement>(null);

  // Focus the card, never a button: the Enter that committed the typed BPM is still being handled, and a
  // button holding focus would take its keypress as a click and choose for the mapper.
  useEffect(() => {
    if (pending != null) cardRef.current?.focus();
  }, [pending]);

  useEffect(() => {
    if (pending == null) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') cancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [pending, cancel]);

  if (pending == null) return null;

  return (
    <div style={backdropStyle} onMouseDown={cancel}>
      <div ref={cardRef} tabIndex={-1} style={cardStyle} role="dialog" aria-label="Change BPM" onMouseDown={e => e.stopPropagation()}>
        <div style={titleBarStyle}>
          <div style={titleStyle}>Change BPM to {pending}</div>
        </div>
        <div style={bodyStyle}>
          <div style={textStyle}>
            This map has objects{current ? ` placed at ${current} BPM` : ''}. How should they follow the new tempo?
          </div>
          <button style={primaryBtnStyle} onClick={() => apply('beats')}>
            <span style={optionTitleStyle}>Keep objects on their beats (recommended)</span>
            <span style={optionTextStyle}>
              Every object, in every difficulty, moves with the tempo so it stays on the same beat as before. Lights and bookmarks are already on their beats.
            </span>
          </button>
          <button style={secondaryBtnStyle} onClick={() => apply('times')}>
            <span style={optionTitleStyle}>Keep objects at their times</span>
            <span style={optionTextStyle}>
              Every object, light and bookmark stays where it is in the song, so it lands on a different beat.
            </span>
          </button>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button style={cancelBtnStyle} onClick={cancel}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Styles ──────────────────────────────────────────────────────────────────

const backdropStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, zIndex: z.modal,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'oklch(0 0 0 / 0.55)', backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
};

const cardStyle: React.CSSProperties = {
  ...ui.dialog,
  width: 440, maxWidth: '92vw', maxHeight: 'calc(100vh - 24px)', outline: 'none',
  display: 'flex', flexDirection: 'column', overflowY: 'auto',
};

const titleBarStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', height: 44, padding: '0 16px',
  borderBottom: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.14)',
};

const titleStyle: React.CSSProperties = {
  fontSize: 12, fontWeight: 700, color: color.accent,
  fontFamily: font.sans, letterSpacing: 1.2, textTransform: 'uppercase',
};

const bodyStyle: React.CSSProperties = { padding: 18, display: 'flex', flexDirection: 'column', gap: 12 };

const textStyle: React.CSSProperties = { fontSize: 12, color: color.inkDim, fontFamily: font.sans, lineHeight: 1.5 };

const optionBase: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4, textAlign: 'left',
  padding: '12px 14px', borderRadius: 14, cursor: 'pointer', fontFamily: font.sans,
};

const primaryBtnStyle: React.CSSProperties = {
  ...optionBase,
  border: '1px solid oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.5)',
  background: 'linear-gradient(135deg, oklch(0.55 calc(0.16 * var(--accent-c)) var(--accent-h) / 0.3), oklch(0.70 calc(0.205 * var(--accent-c)) var(--accent-h) / 0.18))',
  color: 'var(--accent)',
};

const secondaryBtnStyle: React.CSSProperties = {
  ...optionBase,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim,
};

const optionTitleStyle: React.CSSProperties = { fontSize: 12, fontWeight: 700 };

const optionTextStyle: React.CSSProperties = { fontSize: 11, fontWeight: 500, opacity: 0.85, lineHeight: 1.45 };

const cancelBtnStyle: React.CSSProperties = {
  padding: '8px 16px', borderRadius: 999,
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.25)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  color: color.inkDim, fontFamily: font.sans, fontSize: 11, fontWeight: 600, cursor: 'pointer',
};
